import type { AxiosInstance } from 'axios';

// Helper function to wait for a specified time (ms)
const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export class TextToSpeechClient {
  constructor(private authenticatedClient: AxiosInstance) { }

  private pollForCompletion = async (taskId: string): Promise<string> => {
    let attempts = 0;
    let delay = 1000;
    const maxAttempts = 20;

    while (attempts < maxAttempts) {
      await wait(delay);
      const pollResponse = await this.authenticatedClient.get(`/chat/vocalize-async/${taskId}`);
      if (!pollResponse.data.success) {
        throw new Error('Error polling asynchronous TTS task');
      }

      const taskData = pollResponse.data.data;
      if (taskData.TaskStatus === 'completed' && taskData.OutputUri) {
        return taskData.OutputUri;
      }
      if (taskData.TaskStatus === 'failed') {
        throw new Error('Asynchronous TTS task failed');
      }
      attempts++;
      delay = Math.min(delay * 2, 10000);
    }

    throw new Error('Text-to-speech processing did not complete in time.');
  };

  private fetchAsyncAudio = async (text: string): Promise<string> => {
    const asyncResponse = await this.authenticatedClient.post('/chat/vocalize-async', { text });
    if (!asyncResponse.data.success || !asyncResponse.data.data.taskId) {
      throw new Error('Invalid asynchronous TTS data received');
    }
    return this.pollForCompletion(asyncResponse.data.data.taskId);
  };

  fetchTextToSpeechAudio = async (text: string): Promise<string> => {
    if (text.length > 2800) {
      return this.fetchAsyncAudio(text);
    }
    const response = await this.authenticatedClient.post('/chat/vocalize', { queryIdentifier: text });
    if (response.data.success && response.data.data.audio) {
      return response.data.data.audio;
    }
    throw new Error('Invalid audio data received');
  };
}

export default TextToSpeechClient;
